import React, { memo } from 'react';
import { alpha, Box, useTheme } from '@mui/material';
import MainLayout from '../../../shared/ui/layout/MainLayout';
import { borderRadius, getControlBorderColor, layoutGap } from '../../../theme/theme';
import { BodyText, CaptionText, CardTitle, OverlineText, SectionTitle } from '../../../shared/ui/typography';
import {
  EditorSurface,
  InlineReadinessBadge,
  WorkflowStepShell,
} from '../../../shared/ui/workflow/WorkflowStepComponents';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import { getWorkflowSteps } from '../../../shared/config/workflowUi';
import { getStepHelpTooltip } from '../../../shared/config/wizardTooltips';
import { useSelectedOption } from '../../../shared/stores/useUIStore';
import type { WorkflowStepMeta } from '../../../shared/config/workflowUi';
import type { Option } from '../../../shared/types';

interface WelcomePageProps {
  handleContinue: () => void;
  onConvertCurrentSettings: () => void;
  isConverting?: boolean;
}

const StepRow: React.FC<{ item: WorkflowStepMeta; index: number; selectedOption: Option }> = ({ item, index, selectedOption }) => {
  const theme = useTheme();
  const stepHelp = getStepHelpTooltip(item.screen, selectedOption);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '64px minmax(0, 1fr)',
        gap: layoutGap.row,
        alignItems: 'start',
        minWidth: 0,
        py: 0.85,
        borderTop: index === 0 ? 'none' : `1px solid ${alpha(theme.palette.divider, 0.18)}`,
      }}
    >
      <CaptionText sx={{ justifySelf: 'start', px: 0.75, py: 0.32, minWidth: 48, textAlign: 'center', borderRadius: `${borderRadius.full}px`, border: `1px solid ${getControlBorderColor(theme)}`, color: theme.palette.primary.light, backgroundColor: alpha(theme.palette.primary.main, 0.12), fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}>
        Step {index + 1}
      </CaptionText>
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense, minWidth: 0 }}>
          <CardTitle sx={{ color: theme.palette.text.primary, overflowWrap: 'anywhere' }}>{item.label}</CardTitle>
          {stepHelp ? <HelpTooltipTrigger label={`${item.label} step`} help={stepHelp} placement="bottom-start" sx={{ flex: '0 0 auto' }} /> : null}
        </Box>
        <BodyText sx={{ color: theme.palette.text.secondary, lineHeight: 1.45, mt: 0.2 }}>{item.description}</BodyText>
      </Box>
    </Box>
  );
};

const WelcomePage: React.FC<WelcomePageProps> = ({
  handleContinue,
  onConvertCurrentSettings,
  isConverting = false,
}) => {
  const theme = useTheme();
  const selectedOption = useSelectedOption();
  const setupSteps = getWorkflowSteps(selectedOption).filter((step) => step.screen !== 'welcome');
  const setupStepCount = selectedOption ? `${setupSteps.length}` : '6–7';
  const setupStepSummary = `${setupStepCount} steps • 1 profile`;
  const setupPathSummary = selectedOption
    ? `${setupSteps.length} setup steps lead to the PCF mapping profile.`
    : 'Six core setup steps, plus Categories for ISO drawings.';
  const workflowConditionNote =
    selectedOption === 'Iso Drawings Generation'
      ? 'The ISO workflow adds category identifiers after bolt setup.'
      : 'Only the ISO drawing workflow includes category identifiers.';

  return (
    <MainLayout
      title="Set up the PipeEX workflow"
      subtitle="Review what PipeEX will configure before mapping begins."
      showNext={true}
      onNext={handleContinue}
      nextText="Start setup"
      nextDisabled={isConverting}
      showSecondaryAction={true}
      onSecondaryAction={onConvertCurrentSettings}
      secondaryActionText={isConverting ? 'Converting…' : 'Convert current settings'}
      secondaryActionDisabled={isConverting}
      compactWorkflowChrome={true}
      workflowChromeVariant="wizard"
      showRuntimeBadgeInUtilityChrome={false}
    >
      <WorkflowStepShell
        sx={{
          gap: layoutGap.row,
          width: '100%',
          py: 0.75,
        }}
      >
        <EditorSurface sx={{ p: { xs: 1, md: 1.15 }, minWidth: 0 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'auto minmax(0, 1fr) auto' },
              alignItems: 'center',
              gap: layoutGap.row,
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                display: 'grid',
                placeItems: 'center',
                width: { xs: 120, sm: 132 },
                height: { xs: 120, sm: 132 },
                justifySelf: { xs: 'center', sm: 'start' },
                flex: '0 0 auto',
              }}
            >
              {/* The full logo is dark-on-transparent, so it sits on a light
                  circular chip in both themes. Unlike the C# shell caption chip
                  (which uses the badge-only pipeex-logo.png because the text
                  ring is illegible at 28px), the hero is large enough for the
                  official PIPEEX/FIT IT logo. */}
              <Box
                sx={{
                  position: 'relative',
                  width: { xs: 96, sm: 108 },
                  height: { xs: 96, sm: 108 },
                  display: 'grid',
                  placeItems: 'center',
                  borderRadius: '50%',
                  background: 'linear-gradient(155deg, #ffffff 0%, #e9edf5 100%)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="img"
                  src={`${process.env.PUBLIC_URL}/logo.png`}
                  alt="PipeEX logo"
                  sx={{
                    // Scaled up to compensate for the transparent margin baked
                    // into logo.png, so the chip background ends right behind
                    // the badge's outer black outline. The badge also sits
                    // ~1.4% left of the image center, hence the translate;
                    // transform (not width) keeps it optically centered. The
                    // extra ~2% overscan pushes the faint white halo outside
                    // the outline past the chip edge, where overflow clips it.
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                    transform: 'translateX(1.4%) scale(1.13)',
                    userSelect: 'none',
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: layoutGap.dense, flexWrap: 'wrap', mb: 0.35 }}>
                <InlineReadinessBadge label="Revit setup workflow" tone="derived" />
              </Box>
              <SectionTitle>
                Start setup
              </SectionTitle>
              <BodyText
                sx={{
                  color: theme.palette.text.secondary,
                  lineHeight: 1.45,
                  mt: 0.25,
                  maxWidth: 720,
                }}
              >
                Choose the engineering workflow, map its Revit parameters, and review each model update
                before PipeEX opens the mapping workspace.
              </BodyText>
            </Box>
            <Box
              sx={{
                justifySelf: { xs: 'start', sm: 'end' },
                display: 'grid',
                gap: layoutGap.micro,
                px: 1,
                py: 0.6,
                borderRadius: `${borderRadius.md}px`,
                border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
                backgroundColor: alpha(theme.palette.background.default, 0.16),
                minWidth: 134,
              }}
            >
              <OverlineText uppercase={false} sx={{ color: theme.palette.text.secondary, lineHeight: 1.2 }}>
                Setup scope
              </OverlineText>
              <BodyText sx={{ color: theme.palette.text.primary, fontWeight: 700, lineHeight: 1.25 }}>
                {setupStepSummary}
              </BodyText>
            </Box>
          </Box>
        </EditorSurface>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr',
            gap: layoutGap.row,
            alignItems: 'stretch',
          }}
        >
          <EditorSurface
            sx={{
              p: { xs: 1, md: 1.15 },
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: layoutGap.row,
                minWidth: 0,
                pb: 0.7,
                borderBottom: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <SectionTitle component="h2" sx={{ color: theme.palette.text.primary }}>
                  What setup configures
                </SectionTitle>
                <BodyText
                  sx={{ color: theme.palette.text.secondary, lineHeight: 1.45, mt: 0.2 }}
                >
                  {setupPathSummary}
                </BodyText>
              </Box>
              <InlineReadinessBadge label={`${setupStepCount} steps`} tone="set" />
            </Box>
            <Box role="list" sx={{ minWidth: 0 }}>
              {setupSteps.map((item, index) => (
                <StepRow key={item.screen} item={item} index={index} selectedOption={selectedOption} />
              ))}
            </Box>
            <CaptionText sx={{ color: theme.palette.text.secondary, lineHeight: 1.45, mt: 0.75 }}>
              {workflowConditionNote}
            </CaptionText>
          </EditorSurface>

          <EditorSurface
            sx={{
              p: { xs: 0.75, md: 0.85 },
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: layoutGap.dense,
              borderColor: alpha(theme.palette.primary.main, 0.22),
              backgroundColor: alpha(theme.palette.primary.main, 0.04),
            }}
          >
            <CaptionText
              sx={{
                color: theme.palette.text.secondary,
                lineHeight: 1.45,
              }}
            >
              Already configured this Revit project? Use{' '}
              <Box
                component="span"
                sx={{ color: theme.palette.primary.light, fontWeight: 700 }}
              >
                Convert current settings
              </Box>{' '}
              to save the existing local configuration as a profile and open the mapping workspace.
            </CaptionText>
          </EditorSurface>
        </Box>
      </WorkflowStepShell>
    </MainLayout>
  );
};

export default memo(WelcomePage);
